import {
  BoltCustomFieldConfiguration,
  BoltTaskStatuses,
  INewBoltTask,
  useBoltApiClient,
} from "@bolt/core";
import {
  ActionButtons,
  AppToasterV2,
  FieldFormV2,
  FieldFormV2Module,
  Footer,
  SchemaFactoryV2,
  useFieldSchemaV2,
} from "@ucl/library";
import { RefObject, useEffect, useRef, useState } from "react";
import { createBoltTaskBuilder } from "./builders/createBoltTaskBuilder";
import { RichTextEditorsOutputTextTypes } from "../../types/globalTypes";
import { createBoltTaskApiClient } from "../../lib/apiClients/boltTaskApiClient";

export const defaultTask = {
  taskStatus: BoltTaskStatuses.New,
  timezoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
} as INewBoltTask;

export interface CreateBoltTaskFormProps {
  // extends BoltInteraction<
  //   {
  //     onSubmitSuccessfully: (task: INewBoltTask) => Promise<void>;
  //   },
  //   {}
  // > {
  // currentUser: { id: string };
  apiBaseUrl: string;
  getAuthRequestHeader: () => Promise<Record<string, string> | undefined>;
  assigneeOdataUrl: string;
  customFieldConfigurationProps: BoltCustomFieldConfiguration;
  richTextEditorsOutputTextType?: RichTextEditorsOutputTextTypes;
  onSave?: () => void;
  onCancel?: () => void;
  parentEntityId?: string;
  parentEntityType?: string;
}

export const CreateBoltTaskForm: React.FC<CreateBoltTaskFormProps> = ({
  apiBaseUrl,
  getAuthRequestHeader,
  assigneeOdataUrl,
  customFieldConfigurationProps,
  richTextEditorsOutputTextType,
  onCancel,
  onSave,
  parentEntityId,
  parentEntityType,
}) => {
  const boltApiClient = useBoltApiClient();
  const taskApiClient = createBoltTaskApiClient(boltApiClient);
  const taskDialogFormRef: RefObject<FieldFormV2Module<INewBoltTask> | null> =
    useRef<FieldFormV2Module<INewBoltTask>>(null);

  const [schemaFactory, setSchemaFactory] = useState<SchemaFactoryV2>();
  const { getAsyncSchemaFactory } = useFieldSchemaV2("tasks/field-schema");
  const loadingRef = useRef<boolean>(false);

  const getSchema = async () => {
    if (loadingRef.current) {
      return;
    }

    loadingRef.current = true;
    setSchemaFactory(await getAsyncSchemaFactory(boltApiClient));
  };

  useEffect(() => {
    if (loadingRef.current) {
      loadingRef.current = false;
    }
  }, [schemaFactory]);

  useEffect(() => {
    getSchema();
  }, []);

  const handleSubmit = async (task: INewBoltTask) => {
    // Create the task
    await taskApiClient.createTask(task).then(() => {
      onSave?.();
      AppToasterV2.showSuccessAppToaster("Task successfully created.");
    });
  };

  return (
    <div className="tasks-create-dialog-body">
      <div className="tasks-create-dialog-content">
        <div className="tasks-create-dialog-content-form">
          {schemaFactory && (
            <FieldFormV2<INewBoltTask>
              ref={
                taskDialogFormRef as RefObject<FieldFormV2Module<INewBoltTask> | null>
              }
              value={{
                ...defaultTask,
                parentEntityId: parentEntityId,
                parentEntityType: parentEntityType,
                entityType: parentEntityType, // TODO: For now while we support collect entity ids
                entityId: parentEntityId, // TODO: For now while we support collect entity ids
              }}
              onFormSubmit={handleSubmit}
              fieldSchemaFormBuilder={createBoltTaskBuilder(schemaFactory, {
                taskTypeProps: {
                  apiBaseUrl: apiBaseUrl,
                  getAuthRequestHeader: getAuthRequestHeader,
                  entityType: parentEntityType, // TODO: For now while we support collect entity ids
                },
                assigneeProps: {
                  odataUrl: assigneeOdataUrl,
                  getAuthRequestHeader: getAuthRequestHeader,
                },
                taskExecutionTypeData: {
                  apiBaseUrl: apiBaseUrl,
                  getAuthRequestHeader: getAuthRequestHeader,
                },
                fieldConfigurationProps: customFieldConfigurationProps,
                richTextEditorsOutputTextType,
              })}
            />
          )}
        </div>
      </div>

      <Footer>
        <ActionButtons
          actions={[
            {
              text: "Cancel",
              onActionClick: onCancel,
            },
            {
              text: "Create",
              isPrimary: true,
              onActionClick: () => {
                taskDialogFormRef.current?.submit();
              },
            },
          ]}
        />
      </Footer>
    </div>
  );
};
